import { AttributeAbbreviation } from '../services/service-trips-api/types/attribute-abbreviation';

export interface RequiredAttributeGroup {
  key: string;
  codes: ReadonlyArray<AttributeAbbreviation>;
  messageKey: string;
}

/**
 * Groups of attributes where at least one must remain selected on a schedule.
 * Only enforced when the passenger already has attributes from that group.
 *
 * To add a new required group, add an entry with a unique key, the attribute
 * codes that belong together, and a translation key for the validation message.
 */
export const REQUIRED_ATTRIBUTE_GROUPS: ReadonlyArray<RequiredAttributeGroup> = [
  {
    key: 'handover',
    codes: [AttributeAbbreviation.HandOver, AttributeAbbreviation.OkToLeaveAlone],
    messageKey: 'passengerForm.needs.cannotRemoveLastInGroup',
  },
  {
    key: 'vehicle',
    codes: [AttributeAbbreviation.CanTravelByCar, AttributeAbbreviation.NeedsBus],
    messageKey: 'passengerForm.needs.cannotRemoveLastInGroup',
  },
];

export interface MutuallyExclusiveAttributeGroup {
  key: string;
  codes: [AttributeAbbreviation, AttributeAbbreviation];
  messageKey: string;
}

/**
 * Pairs of attributes that contradict each other and must never be selected together.
 *
 * To add a new pair, add an entry with a unique key, the two conflicting attribute
 * codes, and a translation key for the validation message.
 */
// HH/OK appears in both REQUIRED (can't uncheck last) and EXCLUSIVE (can't have both).
// Auto-swap keeps one selected, so both constraints are satisfied together.
export const MUTUALLY_EXCLUSIVE_ATTRIBUTE_GROUPS: ReadonlyArray<MutuallyExclusiveAttributeGroup> = [
  {
    key: 'handover-conflict',
    codes: [AttributeAbbreviation.HandOver, AttributeAbbreviation.OkToLeaveAlone],
    messageKey: 'passengerForm.needs.conflictingAttributes',
  },
  {
    key: 'animal-allergy-conflict',
    codes: [AttributeAbbreviation.Animal, AttributeAbbreviation.AllergyVehicle],
    messageKey: 'passengerForm.needs.conflictingAttributes',
  },
];

/** Returns the code that conflicts with `code`, if any, within the given groups. */
export const getConflictingCode = (
  code: string,
  groups: ReadonlyArray<MutuallyExclusiveAttributeGroup> = MUTUALLY_EXCLUSIVE_ATTRIBUTE_GROUPS
): string | undefined => {
  for (const group of groups) {
    const idx = (group.codes as ReadonlyArray<string>).indexOf(code);
    if (idx !== -1) return group.codes[1 - idx];
  }
  return undefined;
};
